import {CompanyEntity, FreightTypeEntity, Quantity, unitOfMeasureTypes} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, NumberInput, Switch, TextInput} from "@mantine/core";
import {useCallback} from "react";
import {UnitOfMeasureSelect} from "../../select/entity/UnitOfMeasureSelect/UnitOfMeasureSelect";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useTrailerForm} from "./useTrailerForm";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {FreightTypeSelect} from "../../select/entity/FreightTypeSelect/FreightTypeSelect";

export interface TrailerSize {
  size: number;
  maxSize: number;
  units: string
}

export interface TrailerFormProps {
  options?: TrailerFormOptions;
  defaultValues?: TrailerFormDefaultValues;
  onSubmit?: (values: TrailerFormValues) => void;
  submitting?: boolean;
}

export interface TrailerFormOptions {

}

export interface TrailerFormValues {
  name: string;
  barcode?: string;
  size: TrailerSize;
  company: CompanyEntity | string;
  freightType: FreightTypeEntity | string;
  weight?: Partial<Quantity>
  doNotUse?: boolean
}

export interface TrailerFormDefaultValues extends Partial<Omit<TrailerFormValues, 'size'>> {
  size?: Partial<TrailerSize>,
}

export interface TrailerFormRawValues {
  name?: string;
  barcode?: string;
  size?: number;
  maxSize?: number;
  sizeUom?: string
  weight?: number
  weightUom?: string
  company?: string;
  freightType?: string;
  doNotUse?: boolean
}

export type TrailerFormType = UseFormReturnType<TrailerFormRawValues, (values: TrailerFormRawValues) => TrailerFormRawValues>

export const TrailerForm = (props: TrailerFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const region = useApp();

  const {t} = useTranslation(["common"]);

  const form = useTrailerForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {

    if (onSubmit) {
      onSubmit({
        name: values.name!,
        barcode: values.barcode,
        company: values.company!,
        freightType: values.freightType!,
        size: {
          size: values.size!,
          maxSize: values.maxSize!,
          units: values.sizeUom!,
        },
        weight: {
          number: values.weight,
          units: values.weightUom,
        },
        doNotUse: values.doNotUse,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.trailer.form.field.name", "Name")}
            placeholder={t<string>("common.entity.trailer.form.field.name.placeholder", "Name")}
            withAsterisk
            required
            {...form.getInputProps("name")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t("common.entity.trailer.form.field.barcode", "QR Code")}
            placeholder={t<string>("common.entity.trailer.form.field.barcode.placeholder", "QR Code")}
            {...form.getInputProps("barcode")} />
        </Grid.Col>
        <Grid.Col span={12}>
          <CompanySelect
            label={t("common.entity.trailer.form.field.company", "Company")}
            placeholder={t<string>("common.entity.trailer.form.field.company.placeholder", "Company")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("company")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={12}>
          <FreightTypeSelect
            label={t("common.entity.trailer.form.field.freighttype", "Freight Type")}
            placeholder={t<string>("common.entity.trailer.form.field.freighttype.placeholder", "Freight Type")}
            selector={{
              regionId: region.regionId
            }}
            withAsterisk
            required
            clearable
            {...form.getInputProps("freightType")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.trailer.form.field.size", "Capacity")}
            placeholder={t<string>("common.entity.trailer.form.field.size", "Capacity")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            withAsterisk
            required
            {...form.getInputProps("size")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.trailer.form.field.maxsize", "Max Capacity")}
            placeholder={t<string>("common.entity.trailer.form.field.maxsize", "Max Capacity")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            withAsterisk
            required
            {...form.getInputProps("maxSize")} />
        </Grid.Col>
        <Grid.Col span={6}/>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            label={t("common.entity.trailer.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.trailer.form.field.uom.placeholder", "Units")}
            clearable
            {...form.getInputProps("sizeUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Grid grow gutter="xs">
        <Grid.Col span={6}>
          <NumberInput
            label={t("common.entity.trailer.form.field.weight", "Weight")}
            placeholder={t<string>("common.entity.trailer.form.field.weight", "Weight")}
            precision={5}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            {...form.getInputProps("weight")} />
        </Grid.Col>
        <Grid.Col span={6}>
          <UnitOfMeasureSelect
            label={t("common.entity.trailer.form.field.uom", "Units")}
            placeholder={t<string>("common.entity.trailer.form.field.uom.placeholder", "Units")}
            selector={{
              types: [unitOfMeasureTypes.weight]
            }}
            clearable
            {...form.getInputProps("weightUom")} />
        </Grid.Col>
      </Grid>
      <Divider my="sm" variant="dotted"/>
      <Switch
        label={t("common.entity.trailer.form.field.donotuse", "Do Not Use")}
        placeholder={t<string>("common.entity.trailer.form.field.donotuse", "Do Not Use")}
        {...form.getInputProps("doNotUse", {type: 'checkbox'})} />
      <Button type="submit" loading={submitting} fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface TrailerFormModalProps extends TrailerFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const TrailerFormModal = (props: TrailerFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <TrailerForm defaultValues={defaultValues} onSubmit={onSubmit} submitting={submitting}/>
    </Modal>
  );
};
